import React from 'react';
import { PageBackLink, PageHero, PagePanel, PageShell } from '../MarketingPageShell';

const versionContext = require.context('../../versions', false, /\.jsx$/);

const compareVersions = (leftVersion, rightVersion) => {
  const leftParts = leftVersion.split('.').map((part) => Number(part) || 0);
  const rightParts = rightVersion.split('.').map((part) => Number(part) || 0);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftPart = leftParts[index] ?? 0;
    const rightPart = rightParts[index] ?? 0;

    if (leftPart !== rightPart) {
      return rightPart - leftPart;
    }
  }

  return 0;
};

const versionEntries = versionContext
  .keys()
  .map((key) => versionContext(key).default)
  .filter((entry) => entry?.version && entry?.NotesComponent)
  .sort((leftEntry, rightEntry) => compareVersions(leftEntry.version, rightEntry.version));

const UpdatesPage = () => {
  const latestVersion = versionEntries[0];

  return (
    <PageShell>
      <div className="space-y-10 py-4">
        <PageBackLink />
        <div className="mx-auto max-w-5xl space-y-8">
          <PageHero
            eyebrow="Progress"
            title="Product Updates"
          />

          {latestVersion ? (
            <PagePanel className="overflow-hidden border-blue-400/20 bg-gradient-to-br from-blue-500/10 via-slate-900/70 to-cyan-500/5 p-0">
              <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-4 p-8 sm:p-10">
                  <div className="inline-flex items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-200">
                    Latest Release
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm uppercase tracking-[0.3em] text-blue-200/80">Version {latestVersion.version}</p>
                    <h2 className="text-3xl font-semibold text-white sm:text-4xl">What&apos;s new in Toolip</h2>
                    <p className="max-w-2xl text-base leading-7 text-slate-300">
                      Fresh tools, quality-of-life fixes, and product polish all land here.
                    </p>
                    {latestVersion.date ? (
                      <p className="text-sm font-medium uppercase tracking-[0.24em] text-slate-400">
                        Released {latestVersion.date}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="border-t border-white/10 bg-black/20 p-8 sm:p-10 lg:border-l lg:border-t-0">
                  <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5">
                      <div className="text-sm uppercase tracking-[0.25em] text-slate-400">Current</div>
                      <div className="mt-2 text-3xl font-semibold text-white">{latestVersion.version}</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5">
                      <div className="text-sm uppercase tracking-[0.25em] text-slate-400">Tracked Releases</div>
                      <div className="mt-2 text-3xl font-semibold text-white">{versionEntries.length}</div>
                    </div>
                  </div>
                </div>
              </div>
            </PagePanel>
          ) : null}

          <div className="grid gap-6">
            {versionEntries.map(({ version, date, NotesComponent }, index) => {
              return (
                <PagePanel
                  key={version}
                  className="relative overflow-hidden border-white/10 bg-white/[0.045] p-0 transition-colors hover:border-blue-300/20"
                >
                  <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-blue-400 via-cyan-300 to-emerald-300" />
                  <div className="p-8 sm:p-10">
                    <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
                      <div className="space-y-2">
                        <h2 className="text-3xl font-semibold text-white">Version {version}</h2>
                        {date ? <p className="text-sm text-slate-400">{date}</p> : null}
                      </div>
                      <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm text-slate-300">
                        {index === 0 ? 'Newest release' : 'Previous release'}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-slate-950/40 py-6">
                      <NotesComponent />
                    </div>
                  </div>
                </PagePanel>
              );
            })}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default UpdatesPage;
